import { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';
import { Url, URLS } from '@wix/da-url';
import {
  BadgeAwardClickBiEvent,
  BiContextProvidedValue,
  BiData,
  BuyCoreButtonBiEvent,
  GetMorePointsBiEvent,
} from '@wix/da-bi/pkg/events';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import { useBoostPricing } from './useBoostPricing';
import { DurationValue } from '../DurationSlider';
import type { PapiDeviation, PapiTag } from '@wix/da-papi-types';
import { makeXHRCall } from '../../../../../../../simpleXHR/actionCreators';
import { SimpleXHREndpoint, SimpleXHRMethod } from '../../../../../../../simpleXHR/constants';
import { ResourceType } from '../../../../../../../types/resource';
import { addErrorNotification } from '../../../../../../../Notifications/redux/actionCreators';
import { putEntities } from '../../../../../../../redux/entities/actions';
import { normalizeDeviation } from '../../../../../../../redux/normalizr/helpers';
import {
  initialize,
  deinitialize,
  setSelectedDuration as setSelectedDurationAction,
  getRecommendedTags,
  getTags,
  isBoostDataLoading,
  getBoostDataError,
  getCurrentDeviation,
} from '../../redux';

export interface UseBoostCoreModalParams {
  deviation: PapiDeviation;
  onSuccess: () => void;
  onRequestClose: () => void;
}

export interface UseBoostCoreModalReturn {
  // State
  selectedDuration: DurationValue;
  selectedTags: string[];
  isSubmitting: boolean;

  // Data
  availableTags: string[];
  pricingInfo: ReturnType<typeof useBoostPricing>;
  isLoading: boolean;
  error: string | undefined;

  // Actions
  setSelectedDuration: (duration: DurationValue) => void;
  setSelectedTags: (tags: string[]) => void;
  handleSubmit: () => void;
  handleClose: () => void;

  // Computed values
  canSubmit: boolean;
  ctaText: string;
}

export const useBoostCoreModal = ({
  deviation,
  onSuccess,
  onRequestClose,
}: UseBoostCoreModalParams): UseBoostCoreModalReturn => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const logBiEvent = useBiLogger();

  // Local state
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedDuration, setSelectedDuration] = useState<DurationValue>(2);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Redux selectors
  const recommendedTags = useSelector(getRecommendedTags);
  const tags = useSelector(getTags);
  const currentDeviation = useSelector(getCurrentDeviation);
  const isLoading = useSelector(isBoostDataLoading) || !deviation?.extended;
  const error = useSelector(getBoostDataError);

  // Initialize data when deviation changes or on first mount
  useEffect(() => {
    dispatch(initialize(deviation.deviationId, deviation.author?.username || '', deviation.typeId));
    return () => {
      dispatch(deinitialize());
    };
  }, [deviation.deviationId, deviation.author?.username, deviation.typeId, dispatch]);

  // Reset selected tags when deviation changes
  useEffect(() => {
    if (currentDeviation?.deviationId !== deviation.deviationId) {
      setSelectedTags([]);
    }
  }, [deviation.deviationId, currentDeviation?.deviationId]);

  // Get boost restrictions directly from deviation prop
  const boostRestrictions = deviation?.extended?.boostRestrictions;

  const freeBoostsRemaining = boostRestrictions?.freeBoostsRemaining || 0;
  const freeBoostsAllowed = boostRestrictions?.freeBoostsAllowed || 0;
  const pointsBalance = boostRestrictions?.pointsBalance || 0;
  const boostPointsPrice = boostRestrictions?.boostPointsPrice || 125;

  // Calculate pricing for selected duration
  const pricingInfo = useBoostPricing({
    selectedDuration,
    freeBoostsRemaining,
    freeBoostsAllowed,
    pointsBalance,
    boostPointsPrice,
  });

  // Combine deviation tags and recommended tags (max 30)
  const availableTags = useMemo(() => {
    // Extract tag names from tag objects
    const tagNames = tags.map((tag: string | PapiTag) =>
      typeof tag === 'string' ? tag : tag.name
    );
    const recommendedTagNames = recommendedTags.map((tag: string | PapiTag) =>
      typeof tag === 'string' ? tag : tag.name
    );

    const combined = [
      ...tagNames,
      ...recommendedTagNames.filter(tagName => !tagNames.includes(tagName)),
    ];
    return combined.slice(0, 30);
  }, [tags, recommendedTags]);

  // Determine if user can submit
  const canSubmit = !isSubmitting && selectedTags.length <= 3;
  const canAfford = pricingInfo.canAfford;
  const needsPoints = pricingInfo.pointsNeeded > 0;

  // Generate CTA text
  const ctaText = useMemo(() => {
    if (pricingInfo.needsMorePoints) {
      return t('boostDeviation.modal.getMorePoints');
    }
    // if no free and enough points
    if (!freeBoostsRemaining && canAfford) {
      return t('boostDeviation.modal.boostWithPoints', {
        points: pricingInfo.pointsNeeded,
      });
    }
    return t('boostDeviation.modal.boostNow');
  }, [pricingInfo.needsMorePoints, pricingInfo.pointsNeeded, freeBoostsRemaining, canAfford, t]);

  // Handle upgrade to core
  const upgradeToCore = useCallback(() => {
    logBiEvent(
      BiData<BuyCoreButtonBiEvent>({
        evid: 375,
        referral_info: ReferralType.BOOST_DEVIATION_POPUP,
        typeid: deviation.typeId,
        itemid: deviation.deviationId,
      })
    );
    window.location.href = Url.to(URLS.CORE_MEMBERSHIP);
  }, [logBiEvent, deviation.typeId, deviation.deviationId]);

  // Handle boost submission
  const handleSubmit = useCallback(() => {
    if (!canSubmit) return;

    if (canAfford) {
      setIsSubmitting(true);
      dispatch(setSelectedDurationAction(selectedDuration));

      // Log BI event
      logBiEvent(
        BiData<BadgeAwardClickBiEvent>({
          evid: 321,
          badge_type: 'boost',
          badge_typeid: 176,
          sectionname: BiContextProvidedValue,
          itemid: deviation.deviationId,
          typeid: deviation.typeId,
          selected_tags: selectedTags.join(','),
          num_tags: selectedTags.length,
        })
      );

      dispatch(
        makeXHRCall({
          endpoint: SimpleXHREndpoint.GIVE_CORE_BADGE,
          method: SimpleXHRMethod.POST,
          data: {
            typeid: ResourceType.DEVIATION,
            itemid: deviation.deviationId,
            userid: deviation.author?.userId,
            badgetype: 'Boost',
            duration: selectedDuration,
            use_points: needsPoints,
          },
          onSuccess: () => {
            const entities = normalizeDeviation({
              ...deviation,
              boosted: true,
              extended: {
                ...deviation.extended,
                boosted: true,
              },
            });
            dispatch(
              putEntities({
                entities,
              })
            );
            onSuccess();
          },
          onError: () => {
            dispatch(addErrorNotification(t('common.somethingWentWrong')));
            setIsSubmitting(false);
          },
        })
      );
    } else if (pricingInfo.needsMorePoints) {
      logBiEvent(
        BiData<GetMorePointsBiEvent>({
          evid: 504,
          typeid: deviation.typeId,
          widgetname: 'boost_modal',
        })
      );

      window.location.href = Url.to(URLS.POINTS);
    } else {
      upgradeToCore();
    }
  }, [
    canSubmit,
    canAfford,
    needsPoints,
    dispatch,
    deviation,
    selectedTags,
    selectedDuration,
    pricingInfo,
    logBiEvent,
    onSuccess,
    upgradeToCore,
    t,
  ]);

  return {
    // State
    selectedDuration,
    selectedTags,
    isSubmitting,

    // Data
    availableTags,
    pricingInfo,
    isLoading,
    error,

    // Actions
    setSelectedDuration,
    setSelectedTags,
    handleSubmit,
    handleClose: onRequestClose,

    // Computed values
    canSubmit,
    ctaText,
  };
};
